import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useMediaQuery } from '../../../application/hooks/useMediaQuery';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { useAuthStore } from '../../../application/stores/authStore';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { BottomSheet } from '../ui/BottomSheet';
import { Modal } from '../ui/Modal';
import { PasswordResetFlow } from './PasswordResetFlow';

/**
 * Profile → reset / change password. The backend has no "current + new password" endpoint, only the SMS-code
 * reset (E30–E32), so this opens that flow for the member's own phone. A successful reset revokes every session
 * server-side, hence the local sign-out afterwards.
 */
export function ChangePasswordCard({ phone }: { phone: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const close = () => setOpen(false);
  const signOut = () => {
    useAuthStore.getState().clearSession();
    queryClient.clear();
    navigate('/login', { replace: true });
  };
  const flow = <PasswordResetFlow phone={phone} onSignOut={signOut} onBusyChange={setBusy} />;
  const title = t('profile.password.cardTitle');

  return (
    <Card title={title} data-testid="change-password-card">
      <p className="muted pw-intro">{t('profile.password.cardBody')}</p>
      <Button variant="secondary" onClick={() => setOpen(true)} data-testid="change-password-open">
        <KeyRound size={16} /> {t('profile.password.open')}
      </Button>
      {isMobile ? (
        <BottomSheet open={open} title={title} onClose={() => !busy && close()} testId="change-password-sheet">
          {flow}
        </BottomSheet>
      ) : (
        <Modal open={open} title={title} onClose={close} busy={busy}>
          {flow}
        </Modal>
      )}
    </Card>
  );
}
